import React, { useState, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import DropdownMenu, { DropdownItem } from '../DropdownMenu'
import DataTable, { Row, RowData } from '../DataTable'
import UsersList from '../UsersList'
import UserRolesList from '../UserRolesList'
import UserGroupsList from '../UserGroupsList'
import Columns from '../Columns'
import Info from '../Info'
import ContextMenu from '../ContextMenu'
import EditOrganizationDetails, {
  formatRetentionLifetime
} from '../EditOrganizationDetails'
import { IconPickerItem } from '../Form/IconField'
import Spinner from '../Spinner'

import { auth, db } from '../../firebase'
import {
  doc,
  getDoc,
  deleteDoc,
  getDocs,
  collection,
  query,
  where
} from 'firebase/firestore'
import { useError, usePermissions } from '../../hooks'
import useGetUserData from '../../hooks/useGetUserData'

function capitalize(s) {
  return s ? s[0].toUpperCase() + s.slice(1) : ''
}

function DispatchBindingsList({ organizationId }) {
  const setError = useError()
  const [dispatchBindings, setDispatchBindings] = useState(null)
  const [loading, setLoading] = useState(true)

  const fetchDispatchBindings = useCallback(async () => {
    try {
      setLoading(true)
      const querySnapshot = await getDocs(
        collection(db, `organizations/${organizationId}/dispatch-bindings`)
      )
      const bindings = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }))
      setDispatchBindings(bindings)
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [organizationId, setError])

  useEffect(() => {
    fetchDispatchBindings()
  }, [fetchDispatchBindings])

  // ✅ Delete Binding (same confirmation & reload as original code)
  const deleteBinding = useCallback(
    async (dispatchOrganizationId) => {
      if (
        !window.confirm(
          'Are you sure you wish to delete this dispatch binding?'
        )
      ) {
        return
      }

      try {
        await deleteDoc(
          doc(
            db,
            `organizations/${organizationId}/dispatch-bindings/${dispatchOrganizationId}`
          )
        )
        fetchDispatchBindings() // ✅ Reload data after deletion
      } catch (err) {
        setError(err)
      }
    },
    [organizationId, setError, fetchDispatchBindings]
  )

  if (loading) {
    return <Spinner />
  }

  return (
    <DataTable
      columns={['ID', 'Name', 'Actions']}
      className='is-fullwidth is-hoverable is-size-7'>
      {dispatchBindings &&
        dispatchBindings
          .filter((x) => x && x.dispatchOrganization)
          .map(({ dispatchOrganization: { id, name } }) => (
            <Row key={id}>
              <RowData>{id}</RowData>
              <RowData>{name}</RowData>
              <RowData>
                <DropdownMenu label='Actions' small={true}>
                  <DropdownItem onClick={() => deleteBinding(id)}>
                    Delete
                  </DropdownItem>
                </DropdownMenu>
              </RowData>
            </Row>
          ))}
    </DataTable>
  )
}

export default function Organization({ id }) {
  const navigate = useNavigate()
  const [organization, setOrganization] = useState(null)
  // const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false)
  const [loading, setLoading] = useState(true)
  const profile = auth.currentUser
  const { user } = useGetUserData(profile.uid)

  // Dummy permissions for now (replace with real ones)
  // const createUserPermission = { granted: true };
  // const updateOrganizationPermission = { granted: true };
  // const deleteOrganizationPermission = { granted: true };
  // const dataExportPermission = { granted: true };

  const createUserPermission = usePermissions('user', 'create')
  const updateOrganizationPermission = usePermissions(
    'organization',
    'updateAny'
  )
  const deleteOrganizationPermission = usePermissions(
    'organization',
    'deleteAny'
  )
  const dataExportPermission = usePermissions('dataExport', 'readAny')

  // A dispatch binding grants READ of the other organisation, never write.
  // firestore.rules: `allow get: if member() && (platformAdmin() || boundTo(orgId))`
  // but `allow update, delete: if platformAdmin() || (approved() && orgId ==
  // claimOrg())`, and the same split on every subcollection -- so roles, groups
  // and dispatch bindings are owner-only writes too.
  //
  // The permissions below are ROLE grants with no organisation in them, so on
  // a bound organisation's page they said yes and every one of those writes was
  // refused by Firestore. It looked like a working feature until Phase 5: the
  // old rules were `allow read, write: if request.auth != null`, so nothing was
  // enforcing the boundary and the UI never grew a check. Appendix A scopes
  // cross-organisation access to a SHARED INCIDENT and what hangs off it --
  // never to the organisation record.
  const isOwnOrg =
    !!user && String(user.organization?.id ?? '') === String(id)

  const loadOrganization = useCallback(async () => {
    setLoading(true)

    try {
      // ✅ Get organization details
      const snap = await getDoc(doc(db, 'organizations', id))
      if (!snap.exists()) {
        setOrganization(null)
        return
      }

      const orgData = { id: snap.id, ...snap.data() }

      // ✅ Fetch default user type if available
      let defaultUserType = null
      if (orgData.defaultUserTypeId) {
        const userTypeSnap = await getDoc(
          doc(db, 'userTypes', String(orgData.defaultUserTypeId))
        )
        if (userTypeSnap.exists()) {
          defaultUserType = { id: userTypeSnap.id, ...userTypeSnap.data() }
        }
      }

      setOrganization({ ...orgData, defaultUserType })
    } catch (err) {
      console.error('Error loading organization:', err)
      setOrganization(null)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    loadOrganization()
  }, [loadOrganization])

  const deleteOrganization = useCallback(async () => {
    if (!window.confirm('Are you sure you wish to delete this organization?'))
      return
    await deleteDoc(doc(db, 'organizations', String(organization.id)))
    navigate('/organizations/list')
  }, [organization, navigate])

  if (loading) return <Spinner />

  return (
    <div>
      <ContextMenu
        renderTitle={() =>
          organization && <h1 className='title'>{organization.name}</h1>
        }>
        <DropdownMenu label='Actions'>
          {createUserPermission.granted && (
            <DropdownItem
              onClick={() =>
                navigate(`/users/create?organizationId=${organization.id}`)
              }>
              Create a new user
            </DropdownItem>
          )}
          {updateOrganizationPermission.granted  && (
            <>
              <DropdownItem
                onClick={() =>
                  navigate(
                    `/user-roles/create?organizationId=${organization.id}`
                  )
                }>
                Create a new user role
              </DropdownItem>
              <DropdownItem
                onClick={() =>
                  navigate(
                    `/user-groups/create?organizationId=${organization.id}`
                  )
                }>
                Create a new user group
              </DropdownItem>
              <DropdownItem
                onClick={() =>
                  navigate(
                    `/dispatch-bindings/create?organizationId=${organization.id}`
                  )
                }>
                Create a new dispatch binding
              </DropdownItem>
              <DropdownItem onClick={() => setEditing(true)}>
                Edit organization details
              </DropdownItem>
            </>
          )}
          <DropdownItem
            onClick={() =>
              navigate(
                `/data-retention-events?organizationId=${organization.id}`
              )
            }>
            Data retention events
          </DropdownItem>
          {dataExportPermission.granted && (
            <DropdownItem onClick={() => navigate('/data-export/jobs')}>
              Data export
            </DropdownItem>
          )}
          {deleteOrganizationPermission.granted && (
            <DropdownItem onClick={deleteOrganization}>
              Delete organization
            </DropdownItem>
          )}
        </DropdownMenu>
      </ContextMenu>

      {organization && (
        <div className='box'>
          <h2 className='subtitle'>Organization Details</h2>
          <Columns>
            <Info label='ID'>{organization.id}</Info>
            <Info label='Name'>{organization.name}</Info>
            <Info label='Data Retention Lifetime'>
              {formatRetentionLifetime(organization.dataRetentionLifetime)}
            </Info>
            <Info label='Time Zone'>{organization.timezone || 'N/A'}</Info>
            <Info label='Status'>{capitalize(organization.status)}</Info>
            <Info label='Default User Type'>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center'
                }}>
                <IconPickerItem
                  icon={organization.defaultUserType?.icon}
                  size={24}
                  color={organization.defaultUserType?.color}
                />
                {organization.defaultUserType?.name}
              </div>
            </Info>
            <Info label='Created On'>
              {organization.createdAt
                ? new Date(
                    organization.createdAt.seconds
                      ? organization.createdAt.seconds * 1000
                      : organization.createdAt
                  ).toLocaleDateString()
                : 'N/A'}
            </Info>
            <Info label='Emergency Phone'>
              {organization.emergencyContactPhone || 'N/A'}
            </Info>
          </Columns>
          {editing && (
            <EditOrganizationDetails
              organization={organization}
              onFinishEditing={() => {
                loadOrganization()
                setEditing(false)
              }}
            />
          )}
        </div>
      )}

      <div className='box'>
        <h2 className='subtitle'>Users</h2>
        <UsersList organizationId={parseInt(id)} />
      </div>
      <div className='box'>
        <h2 className='subtitle'>User Groups</h2>
        <UserGroupsList organizationId={id} />
      </div>
      <div className='box'>
        <h2 className='subtitle'>User Roles</h2>
        <UserRolesList organizationId={id} />
      </div>
      <div className='box'>
        <h2 className='subtitle'>Dispatch Bindings</h2>
        <DispatchBindingsList organizationId={id} />
      </div>
    </div>
  )
}
